import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentUploader } from '../components/DocumentUploader.js';
import { api } from '../lib/api.js';
import { useDemoMode } from '../context/DemoModeContext.js';

interface EvidenceRow {
  id: string;
  evidenceType: string;
  fileName: string;
  extractionStatus: 'PENDING' | 'EXTRACTING' | 'COMPLETED' | 'FAILED';
  extractedFields: Record<string, { value: unknown; confidence: number }> | null;
  extractionConfidence: number | null;
  isHandwritten: boolean;
  aiPromptContextId: string | null;
}

interface PendingEvidence {
  evidenceId: string;
  fileName: string;
}

export function DemoExtractionPage() {
  const { t } = useTranslation();
  const { persona } = useDemoMode();
  const testClaimId = `test-claim-${persona.userId}`;

  const [pendingList, setPendingList] = useState<PendingEvidence[]>([]);
  const [results, setResults] = useState<EvidenceRow[]>([]);
  const pollRefs = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  const handleUploaded = ({ evidenceId }: { evidenceId: string }, file: File) => {
    setPendingList((prev) => [...prev, { evidenceId, fileName: file.name }]);
    startPolling(evidenceId);
  };

  const startPolling = (evidenceId: string) => {
    if (pollRefs.current.has(evidenceId)) return;

    const timer = setInterval(async () => {
      try {
        const row = await api.get<EvidenceRow>(`/evidence/${evidenceId}`);
        if (row.extractionStatus === 'COMPLETED' || row.extractionStatus === 'FAILED') {
          clearInterval(timer);
          pollRefs.current.delete(evidenceId);
          setPendingList((prev) => prev.filter((p) => p.evidenceId !== evidenceId));
          setResults((prev) => [row, ...prev.filter((r) => r.id !== evidenceId)]);
        }
      } catch {
        // silently retry
      }
    }, 2000);

    pollRefs.current.set(evidenceId, timer);
  };

  // Cleanup on unmount
  useEffect(() => {
    const refs = pollRefs.current;
    return () => {
      refs.forEach((t) => clearInterval(t));
    };
  }, []);

  return (
    <div className="space-y-8 max-w-[860px]">
      <div>
        <span className="inline-block text-[11px] font-semibold tracking-[0.22em] uppercase text-copper mb-2">
          {t('tenancy.devLabel')}
        </span>
        <h1 className="font-serif italic text-3xl text-ink">
          {t('extraction.title')}
        </h1>
        <p className="text-slate-500 text-sm mt-1">{t('extraction.subtitle')}</p>
      </div>

      <div className="bg-bone border border-hairline rounded-xl p-6 space-y-4">
        <h2 className="font-serif italic text-lg text-ink">{t('extraction.upload')}</h2>
        <DocumentUploader claimId={testClaimId} onUploaded={handleUploaded} />
      </div>

      {pendingList.length > 0 && (
        <div className="bg-copper-soft border border-copper/20 rounded-xl p-4">
          <p className="text-sm font-medium text-copper-deep">{t('extraction.processing')}</p>
          <ul className="mt-2 space-y-1">
            {pendingList.map(({ evidenceId, fileName }) => (
              <li key={evidenceId} className="text-xs font-mono text-slate-600 flex items-center gap-2">
                <span className="inline-block w-3 h-3 border-2 border-copper border-t-transparent rounded-full animate-spin" />
                {fileName}
              </li>
            ))}
          </ul>
        </div>
      )}

      {results.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-serif italic text-xl text-ink">{t('extraction.result')}</h2>
          {results.map((row) => (
            <ExtractionResultCard key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function ExtractionResultCard({ row }: { row: EvidenceRow }) {
  const { t } = useTranslation();

  if (row.extractionStatus === 'FAILED') {
    return (
      <div className="bg-bone border border-danger/40 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-danger font-mono font-bold">✗</span>
          <span className="font-medium text-ink">{row.fileName}</span>
        </div>
        <p className="text-sm text-danger">{t('extraction.failed')}</p>
      </div>
    );
  }

  const fields = row.extractedFields ?? {};

  return (
    <div className="bg-bone border border-hairline rounded-xl p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium text-ink">{row.fileName}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[11px] font-mono uppercase tracking-[0.15em] text-copper bg-copper-soft px-2 py-0.5 rounded">
              {row.evidenceType}
            </span>
            {row.isHandwritten && (
              <span className="text-[11px] font-mono uppercase tracking-[0.15em] text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                {t('extraction.handwritten')}
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-slate-500">{t('extraction.confidence')}</p>
          <p className="font-mono text-lg font-semibold text-ink">
            {row.extractionConfidence != null
              ? `${(row.extractionConfidence * 100).toFixed(0)}%`
              : '—'}
          </p>
        </div>
      </div>

      {Object.keys(fields).length > 0 && (
        <div>
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-2">
            {t('extraction.fields')}
          </p>
          <div className="space-y-1">
            {Object.entries(fields).map(([key, field]) => (
              <FieldRow key={key} name={key} field={field} />
            ))}
          </div>
        </div>
      )}

      {row.aiPromptContextId && (
        <p className="text-[10px] font-mono text-slate-300">
          ctx: {row.aiPromptContextId}
        </p>
      )}
    </div>
  );
}

function FieldRow({ name, field }: { name: string; field: { value: unknown; confidence: number } }) {
  const displayValue =
    field.value === null || field.value === undefined
      ? '—'
      : typeof field.value === 'object'
        ? JSON.stringify(field.value)
        : String(field.value);

  const confPct = typeof field.confidence === 'number' ? field.confidence : 0;
  const confColor =
    confPct >= 0.8 ? 'text-success' : confPct >= 0.5 ? 'text-copper' : 'text-danger';

  return (
    <div className="flex items-baseline justify-between py-1 border-b border-hairline last:border-0 gap-4">
      <span className="text-xs font-mono text-slate-500 shrink-0">{name}</span>
      <div className="flex items-baseline gap-2 min-w-0">
        <span className="text-sm text-ink truncate">{displayValue}</span>
        <span className={`text-[10px] font-mono shrink-0 ${confColor}`}>
          {(confPct * 100).toFixed(0)}%
        </span>
      </div>
    </div>
  );
}
